import { useToast } from "@chakra-ui/react";
import { PropsWithChildren, createContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { useOnlyOnce } from "@nens/react/hooks";

import NoInternet from "./no-internet.png";

export const NetworkContext = createContext<{
  isOnline: boolean;
  noNetworkImageSrc: string | null;
}>({ isOnline: true, noNetworkImageSrc: null });

export function NetworkProvider({ children }: PropsWithChildren) {
  const intl = useIntl();
  const toast = useToast();

  const [noNetworkImageSrc, setNoNetworkImageSrc] = useState<string | null>(null);

  useOnlyOnce(() => {
    fetch(NoInternet)
      .then((response) => response.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => setNoNetworkImageSrc(reader.result as string);
        reader.readAsDataURL(blob);
      });
  });

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast({
        title: intl.formatMessage({
          id: "networkstatus.online",
          defaultMessage: "Back online!",
        }),
        description: intl.formatMessage({
          id: "networkstatus.online.description",
          defaultMessage: "You are now connected to the internet.",
        }),
        status: "success",
        duration: 3000,
      });
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast({
        title: intl.formatMessage({
          id: "networkstatus.offline",
          defaultMessage: "You are offline!",
        }),
        description: intl.formatMessage({
          id: "networkstatus.offline.description",
          defaultMessage: "Please check your internet connection.",
        }),
        status: "error",
        duration: 3000,
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [intl, toast]);

  return (
    <NetworkContext.Provider value={{ isOnline, noNetworkImageSrc }}>
      {children}
    </NetworkContext.Provider>
  );
}
